import {
  AMPLITUDE_PRODUCTION_API_KEY,
  AMPLITUDE_STAGING_API_KEY,
} from './amplitude/constants';

interface DomainMeta {
  environment: 'dev' | 'stage' | 'uat' | 'prod';
  monolithOrigin: string;
  tenant: string;
  apiKey: {
    amplitude?: string;
  };
}

// Searches for any domain such as careers.ap-1.fountain.com. Will not match `careers.fountain.com`.
const FOUNTAIN_DOMAIN_MATCHER = /^careers\.[^.]*\.fountain\.com/;
// Matches domains like "careers.sandbox.amazon-na.fountain.com"
// Should also match "careers.sandbox.brandsafway.fountain.com"
const FOUNTAIN_SANDBOX_SUBDOMAIN_MATCHER =
  /^careers\.sandbox\.[a-zA-Z0-9-]+\.fountain\.com/;
// Matches the staging domains such as careers.staging-use-05.fountain.com
const FOUNTAIN_STAGING_DOMAIN_MATCHER = /^careers\.staging[^.]*\.fountain\.com/;
// Matches the RWX dev sandbox host, e.g. career-site-ui-alice--fountain.r1.rwx.run
// Captures the slug and the org/region suffix so the monolith origin
// (hire-{slug}--…) can be derived. The region segment is optional: RWX's
// canonical, task-cache-key-pinned hostnames omit it.
const FOUNTAIN_RWX_SANDBOX_MATCHER =
  /^career-site-ui-(.+)(--fountain\.(?:[a-z0-9-]+\.)?rwx\.run)$/;

const FOUNTAIN_DEV_DOMAINS = [
  'careers.dev-01.fountain.com',
  'careers.dev-02.fountain.com',
  'careers.faut-02.fountain.com',
  'careers.faut-03.fountain.com',
];

const FOUNTAIN_UAT_DOMAINS = [
  'careers.uat-01.fountain.com',
  'careers.faut-01.fountain.com',
];

const isStagingDomain = (hostname: string) => {
  if (FOUNTAIN_STAGING_DOMAIN_MATCHER.test(hostname)) {
    return true;
  }
  return false;
};

const isUatDomain = (hostname: string) => {
  if (FOUNTAIN_UAT_DOMAINS.includes(hostname)) {
    return true;
  }
  return false;
};

const isDevDomain = (hostname: string) => {
  if (FOUNTAIN_DEV_DOMAINS.includes(hostname)) {
    return true;
  }
  return false;
};

const isSandboxDomain = (hostname: string): boolean =>
  FOUNTAIN_SANDBOX_SUBDOMAIN_MATCHER.test(hostname);

export const inferDomainMeta = (hostname: string): DomainMeta => {
  if (hostname === 'localhost') {
    return {
      environment: 'dev',
      monolithOrigin: `http://localhost:${
        process.env.NODE_ENV === 'test' ? 3023 : 3000
      }`,
      tenant: 'local',
      apiKey: {
        amplitude: AMPLITUDE_STAGING_API_KEY,
      },
    };
  }

  if (hostname.endsWith('.chromatic.com')) {
    return {
      environment: 'dev',
      monolithOrigin: 'http://localhost:3000',
      tenant: 'storybook',
      apiKey: {},
    };
  }

  if (isDevDomain(hostname)) {
    return {
      environment: 'dev',
      monolithOrigin: `https://${hostname.slice(8)}`,
      tenant: hostname.split('.')[1],
      apiKey: {},
    };
  }

  if (isStagingDomain(hostname)) {
    return {
      environment: 'stage',
      monolithOrigin: `https://${hostname.slice(8)}`,
      tenant: hostname.split('.')[1],
      apiKey: {
        amplitude: AMPLITUDE_STAGING_API_KEY,
      },
    };
  }

  if (isUatDomain(hostname)) {
    return {
      environment: 'uat',
      monolithOrigin: `https://${hostname.slice(8)}`,
      tenant: hostname.split('.')[1],
      apiKey: {
        amplitude: AMPLITUDE_STAGING_API_KEY,
      },
    };
  }

  if (isSandboxDomain(hostname)) {
    const subdomain = hostname.split('.')[2];

    return {
      environment: 'prod',
      monolithOrigin: `https://${hostname.slice(8)}`,
      tenant: subdomain,
      apiKey: {
        amplitude: AMPLITUDE_STAGING_API_KEY,
      },
    };
  }

  if (hostname === 'careers.fountain.com') {
    return {
      environment: 'prod',
      monolithOrigin: 'https://web.fountain.com',
      tenant: 'fountain',
      apiKey: {
        amplitude: AMPLITUDE_PRODUCTION_API_KEY,
      },
    };
  }

  if (FOUNTAIN_DOMAIN_MATCHER.test(hostname)) {
    return {
      environment: 'prod',
      monolithOrigin: `https://${hostname.slice(8)}`,
      tenant: hostname.split('.')[1],
      apiKey: {
        amplitude: AMPLITUDE_PRODUCTION_API_KEY,
      },
    };
  }

  // RWX dev sandbox: the career site is served at
  // `career-site-ui-{slug}--fountain.r1.rwx.run`, with the monolith in the same
  // sandbox at `hire-{slug}--fountain.r1.rwx.run`. Treat as a dev environment so
  // routing does not require the commit-hash path prefix used on staging.
  const rwxSandboxMatch = hostname.match(FOUNTAIN_RWX_SANDBOX_MATCHER);
  if (rwxSandboxMatch) {
    const [, slug, orgSuffix] = rwxSandboxMatch;
    return {
      environment: 'dev',
      monolithOrigin: `https://hire-${slug}${orgSuffix}`,
      tenant: slug,
      apiKey: {
        amplitude: AMPLITUDE_STAGING_API_KEY,
      },
    };
  }

  if (hostname.endsWith('.modal.host')) {
    // Open-Inspect / Modal sandbox tunnel (e.g. ta-<id>-<port>-<rand>.w.modal.host). Served behind
    // an nginx reverse proxy that routes API paths to the monolith on the SAME origin, so use a
    // same-origin (empty) monolith origin. Mirrors the RWX sandbox case above.
    return {
      environment: 'dev',
      monolithOrigin: '',
      tenant: 'modal-sandbox',
      apiKey: {
        amplitude: AMPLITUDE_STAGING_API_KEY,
      },
    };
  }

  throw new Error('Unable to map hostname to a known domain');
};

export const domainMeta = inferDomainMeta(window.location.hostname);
